/**
 * 通用 UI 组件 - 轻量、零依赖（不引 antd/mui，保持包小）
 */

import type { ReactNode } from 'react'

/** 状态徽章（按状态着色） */
export function Badge({ status, label }: { status?: string; label?: string }) {
  const text = label ?? status ?? ''
  const color = colorFor(status ?? '')
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 600,
        background: color.bg,
        color: color.fg,
      }}
    >
      {text}
    </span>
  )
}

function colorFor(s: string): { bg: string; fg: string } {
  // 订单状态只有 PAID（绿）/ REFUNDED（红）；其余是 settlements/invoices/logistics/合作 的子状态
  const success = ['PAID', 'ACTIVE', 'SUCCESS', 'ISSUED', 'SIGNED', 'UPLOADED', 'DELIVERED']
  const danger = ['REFUNDED', 'REJECTED', 'FAILED', 'SUSPENDED', 'CANCELLED', 'TERMINATED', 'REVERSED', 'RED_FLUSHED']
  const info = ['NOT_ISSUED', 'RED_FLUSHING', 'REVERSING']
  const warn = ['PENDING', 'NEGOTIATING', 'ISSUING', 'EXECUTING']
  if (success.includes(s)) return { bg: '#dcfce7', fg: '#166534' }
  if (danger.includes(s)) return { bg: '#fee2e2', fg: '#991b1b' }
  if (info.includes(s)) return { bg: '#dbeafe', fg: '#1e40af' }
  if (warn.includes(s)) return { bg: '#fef9c3', fg: '#854d0e' }
  return { bg: '#f3f4f6', fg: '#374151' }
}

/** 简单表格 */
export function Table<T>({
  rows,
  columns,
  empty = '暂无数据',
}: {
  rows: T[]
  columns: Array<{ key: string; title: string; render?: (row: T) => ReactNode }>
  empty?: string
}) {
  if (rows.length === 0) {
    return <div style={{ padding: 24, color: '#888', textAlign: 'center' }}>{empty}</div>
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
          {columns.map((c) => (
            <th key={c.key} style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>
              {c.title}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {columns.map((c) => (
              <td key={c.key} style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** 按钮 */
export function Button({
  children,
  onClick,
  variant = 'primary',
  disabled,
  size = 'md',
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'default' | 'danger'
  disabled?: boolean
  size?: 'sm' | 'md'
}) {
  const bg = variant === 'primary' ? '#2563eb' : variant === 'danger' ? '#dc2626' : '#fff'
  const color = variant === 'primary' || variant === 'danger' ? '#fff' : '#374151'
  const border = variant === 'default' ? '1px solid #d1d5db' : '1px solid transparent'
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: size === 'sm' ? '4px 10px' : '8px 16px',
        background: bg,
        color,
        border,
        borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: size === 'sm' ? 12 : 14,
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  )
}

/** 卡片 */
export function Card({ title, children, action }: { title?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', marginBottom: 16 }}>
      {title && (
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #e5e7eb',
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{title}</span>
          {action}
        </div>
      )}
      <div style={{ padding: 16 }}>{children}</div>
    </div>
  )
}

/** 加载/错误态 */
export function Loading({ error }: { error?: string }) {
  if (error) return <div style={{ padding: 16, color: '#dc2626' }}>加载失败：{error}</div>
  return <div style={{ padding: 16, color: '#888' }}>加载中...</div>
}

/** 异步数据 hook */
import { useEffect, useState } from 'react'
export function useApi<T>(fetcher: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [refreshKey, setRefreshKey] = useState(0)
  const refresh = () => setRefreshKey((k) => k + 1)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetcher()
      .then((d) => !cancelled && (setData(d), setError('')))
      .catch((e) => !cancelled && setError((e as Error).message))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, refreshKey])

  return { data, error, loading, refresh }
}

/** 金额格式化 */
export function money(n: number | string): string {
  const num = typeof n === 'string' ? parseFloat(n) : n
  return `¥${num.toFixed(2)}`
}

/** 日期格式化 */
export function time(s: string): string {
  if (!s) return '-'
  return new Date(s).toLocaleString('zh-CN', { hour12: false })
}

export function formatScope(scope: unknown): string {
  if (Array.isArray(scope)) return scope.join(',')
  if (typeof scope !== 'string') return ''

  const value = scope.trim()
  if (!value) return ''

  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) return parsed.join(',')
  } catch {
    // Also accept Postgres array text such as {服装,日用品}.
  }

  return value.replace(/^\{|\}$/g, '').split(',').filter(Boolean).join(',')
}
